import { Link } from "react-router-dom";
import { Radio, Mic, Phone, PhoneIncoming, PhoneOutgoing, ArrowRight, Database, FolderOpen, AudioLines, AlertCircle, CheckCircle2 } from "lucide-react";
import { Tilt3D } from "./Tilt3D";
import { formatDate } from "../lib/constants";

const Metric = ({ label, value, testId }) => (
  <div data-testid={testId} className="min-w-0">
    <p className="text-2xl font-bold tracking-tight font-mono">{value ?? "—"}</p>
    <p className="truncate text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
  </div>
);

const Card = ({ title, subtitle, icon: Icon, accent, to, cta, testId, children }) => (
  <Tilt3D data-testid={testId} className="surface stat-3d flex h-full flex-col p-5">
    <div className="flex items-start justify-between gap-3">
      <div className="flex items-center gap-3">
        <span className="flex h-10 w-10 items-center justify-center rounded-lg" style={{ background: `${accent}22`, color: accent }}><Icon className="h-5 w-5" /></span>
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground">{title}</p>
          <p className="text-sm font-semibold">{subtitle}</p>
        </div>
      </div>
    </div>
    <div className="mt-4 flex-1">{children}</div>
    <Link to={to} data-testid={`${testId}-link`} className="mt-4 inline-flex items-center gap-1.5 self-start text-xs font-semibold hover:underline" style={{ color: accent }}>
      {cta} <ArrowRight className="h-3.5 w-3.5" />
    </Link>
  </Tilt3D>
);

// Riepilogo dei moduli abilitati (Sales Help · MyPailot · Chiamate) in testa alla Dashboard
export const ModuleOverview = ({ data }) => {
  if (!data) return null;
  const { sales_help: s, mypailot: m, chiamate: c } = data;
  const n = [s, m, c].filter(Boolean).length;
  if (!n) return null;
  return (
    <div data-testid="module-overview" className={`stagger persp relative z-10 grid gap-4 ${n === 3 ? "lg:grid-cols-3" : n === 2 ? "lg:grid-cols-2" : ""}`}>
      {s && (
        <Card testId="module-sales-help" title="Sales Help" subtitle="Conoscenza, manuale, coach e Live" icon={Radio} accent="#60A5FA" to="/conoscenza" cta="Apri la Knowledge Base">
          <div className="grid grid-cols-3 gap-3">
            <Metric testId="sh-contenuti" label="Contenuti" value={s.contenuti} />
            <Metric testId="sh-conoscenze" label="Conoscenze" value={s.conoscenze} />
            <Metric testId="sh-call-live" label="Call Live" value={s.call_live} />
          </div>
          <p className="mt-3 flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1"><FolderOpen className="h-3 w-3" /> {s.indicizzati} indicizzati</span>
            <span className="inline-flex items-center gap-1"><Database className="h-3 w-3" /> {s.script} script suggeribili</span>
            {s.ultima_call_live && <span>· ultima call {formatDate(s.ultima_call_live)}</span>}
          </p>
        </Card>
      )}
      {m && (
        <Card testId="module-mypailot" title="MyPailot" subtitle="Voci degli operatori" icon={Mic} accent="#34D399" to="/mypailot" cta="Gestisci le voci">
          <div className="grid grid-cols-2 gap-3">
            <Metric testId="mp-voci" label="Voci registrate" value={m.voci} />
            <Metric testId="mp-clonate" label="Clonate" value={m.clonate} />
          </div>
          <p className="mt-3 text-xs text-muted-foreground">
            {m.voce_agente ? <span className="inline-flex items-center gap-1 text-[#FCD990]"><AudioLines className="h-3 w-3" /> L'agente vocale parla con la voce di {m.voce_agente}</span>
              : m.voci === 0 ? "Nessuna voce ancora: registra la prima." : m.ultime.map((v) => v.nome + (v.ruolo ? ` · ${v.ruolo}` : "")).join(", ")}
          </p>
        </Card>
      )}
      {c && (
        <Card testId="module-chiamate" title="Chiamate" subtitle="Agente vocale AI in entrata e in uscita" icon={Phone} accent="#F5B942" to="/chiamate" cta="Vai alle chiamate">
          <div className="grid grid-cols-3 gap-3">
            <Metric testId="ch-totale" label="Totali" value={c.totale} />
            <Metric testId="ch-inbound" label="In entrata" value={c.inbound} />
            <Metric testId="ch-outbound" label="In uscita" value={c.outbound} />
          </div>
          <p className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
            {c.assistente_attivo ? <span className="inline-flex items-center gap-1 text-emerald-300"><CheckCircle2 className="h-3 w-3" /> Attivo su {c.numero}</span> : <span className="inline-flex items-center gap-1 text-amber-300"><AlertCircle className="h-3 w-3" /> Assistente da configurare</span>}
            {c.in_corso > 0 && <span className="text-amber-300">{c.in_corso} in corso</span>}
            {c.errori > 0 && <span className="text-red-300">{c.errori} in errore</span>}
          </p>
          {c.ultime.length > 0 && (
            <ul data-testid="ch-ultime" className="mt-3 space-y-1.5">
              {c.ultime.map((k) => (
                <li key={k.call_id} className="flex items-center justify-between gap-2 text-xs">
                  <span className="inline-flex min-w-0 items-center gap-1.5">
                    {k.direzione === "inbound" ? <PhoneIncoming className="h-3 w-3 shrink-0 text-teal-300" /> : <PhoneOutgoing className="h-3 w-3 shrink-0 text-primary" />}
                    <span className="truncate font-mono">{k.numero || "—"}</span>
                  </span>
                  <span className={`shrink-0 ${k.errore ? "text-red-300" : "text-muted-foreground"}`}>{k.errore ? "errore" : k.stato === "ended" ? "terminata" : k.stato} · {formatDate(k.created_at)}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      )}
    </div>
  );
};
